export const treeFlat = (items) => {
	const arr = [];
	items.forEach((e) => {
		arr.push(e);
		if (e.children) {
			arr.push(...treeFlat(e.children));
		}
	});
	return arr;
};

export const makeTree = (list) => {
	const findChildren = (id) => {
		return list
			.filter((e) => e.parentId === id)
			.map((e) => ({
				...e,
				children: findChildren(e.id),
			}));
	};

	const arr = list
		.filter((e) => !e.parentId)
		.map((e) => ({
			...e,
			children: findChildren(e.id),
		}));

	return arr;
};

export function removeEmpty(obj) {
	return Object.fromEntries(
		Object.entries(obj).filter(
			([_, v]) => v != null && v !== undefined && v.length !== 0 && !Number.isNaN(v),
		),
	);
}

export function getEnumByValue(value, theEnum) {
	return Object.values(theEnum).find((e) => e.value === value)?.text;
}

/**
 * @param {any} obj - The object to validate.
 * @param {Array<{ require: string, message: string }|{ for: string[], fun: (value: any) => boolean,message: string}>} validations - An array of validation rules.
 * @param {boolean} withMessages - If true, returns an array of error messages; otherwise, returns a boolean indicating overall validity.
 * @returns {boolean|Array<string>} - Validation result or error messages.
 */
export function validating(obj, validations, withMessages) {
	let isValid = withMessages ? [] : true;

	const check = (item, message) => {
		if (withMessages) {
			if (!item) isValid.push(message);
		} else {
			isValid &= !!item;
		}
	};

	for (const valid of validations) {
		if (valid.require) {
			check(obj[valid.require], valid.message);
		}

		if (typeof valid.for === 'object' && valid.for.length) {
			const item = valid.fun(...valid.for.map((e) => obj[e]));
			check(item, valid.message);
		}
	}

	return isValid;
}

export const showErrorMessagesFun = (vm) => (e) => {
	if (!e.length) return;
	vm.$eventBus.$emit('show-snackbar', e.join(', '), {
		error: true,
	});
};

export class TableLocale {
	constructor(
		headers,
		{ resetTo = null, validation = [], showErrorMessages = false, showErrorMessagesFun = () => {} } = {},
	) {
		this.inputs = {};
		this.headers = headers;
		this.items = [];
		this.counter = -1;
		this.editId = null;
		this.ref = null;

		this.resetTo = resetTo;
		this.validation = validation;
		this.showErrorMessages = showErrorMessages;
		this.showErrorMessagesFun = showErrorMessagesFun;
	}

	validate() {
		let isValid;
		if (this.showErrorMessages) {
			const messages = validating(this.inputs, this.validation, this.showErrorMessages);
			this.showErrorMessagesFun(messages);
			isValid = !messages.length;
		} else {
			isValid = !!validating(this.inputs, this.validation, this.showErrorMessages);
		}
		return isValid;
	}

	add() {
		if (!this.validate()) return;

		this.items.push({ ...this.inputs, id: ++this.counter });
		this.resetTo ? this.ref.resetInputs(this.resetTo) : this.ref.resetInputs();
		this.ref.resetInputsFocus();
	}

	update() {
		if (!this.validate()) return;

		for (const key in this.inputs) {
			if (Object.hasOwnProperty.call(this.inputs, key)) {
				this.items[this.editId][key] = this.inputs[key];
			}
		}

		this.editId = null;
		this.ref.resetEdit();
		this.resetTo ? this.ref.resetInputs(this.resetTo) : this.ref.resetInputs();
		this.ref.resetInputsFocus();
	}

	delete(i) {
		this.items.splice(i, 1);
	}
}

export class TableWithApi extends TableLocale {
	constructor(vm, headers, { get, add = '', remove = '' }, { ...options } = {}) {
		super(headers, options);

		this.storeGet = get;
		this.storeAdd = add;
		this.storeDelete = remove;

		this.loadingFetching = false;
		this.loadingAdding = false;

		this.vm = vm;
		this.fetch();
	}

	fetch() {
		this.loadingFetching = true;
		this.vm.$store
			.dispatch(this.storeGet)
			.then((res) => {
				this.items = res;
			})
			.finally(() => {
				this.loadingFetching = false;
			});
	}

	addApi({ id, ...options }) {
		this.loadingAdding = true;
		this.vm.$store
			.dispatch(this.storeAdd, { id, ...options })
			.then(() => {
				this.vm.$eventBus.$emit('show-snackbar', this.vm.$T('snackbar.addingDone'), { done: true });
				this.fetch();
			})
			.catch((err) => {
				err.default && err.default(this.vm.$T('messages.fail-to-add'), false);
			})
			.finally(() => {
				this.loadingAdding = false;
			});
	}

	add() {
		if (!this.validate()) return;

		this.addApi({ id: this.editId, ...this.inputs });

		this.resetTo ? this.ref.resetInputs(this.resetTo) : this.ref.resetInputs();
		this.ref.resetInputsFocus();
		this.ref.resetEdit();
	}

	update() {
		if (!this.validate()) return;

		this.add();
	}

	delete(id) {
		this.loadingAdding = true;
		this.vm.$store
			.dispatch(this.storeDelete, id)
			.then(() => {
				this.vm.$eventBus.$emit('show-snackbar', this.vm.$T('snackbar.deleteDone'), { done: true });
				this.fetch();
			})
			.catch((err) => {
				err.default && err.default(this.vm.$T('snackbar.deleteFail'), false);
			})
			.finally(() => {
				this.loadingAdding = false;
			});
	}
}

export function digInObject(obj, key, spliter = '.') {
	const keys = key.split(spliter);

	for (const k of keys) {
		if (obj[k] === null || obj[k] === undefined) {
			return null;
		}
		obj = obj[k];
	}

	return obj;
}

export function digAndAssigns(obj, key, newObj, spliter = '.') {
	let temp = obj;

	const keys = key.split(spliter);
	const lastKey = keys.pop();

	for (const k of keys) {
		if (!temp[k] || typeof temp[k] !== 'object') {
			temp[k] = {};
		}
		temp = temp[k];
	}

	temp[lastKey] = newObj;

	return obj;
}

export function getObjectFromJSON(json, key, spliter = '.') {
	if (!json || !JSON.parse(json)) return null;

	return digInObject(JSON.parse(json), key, spliter);
}
export function setObjectToJSON(json, key, newObj, spliter = '.') {
	if (!json) json = '{}';

	return JSON.stringify(digAndAssigns(JSON.parse(json), key, newObj, spliter));
}
